import { leagueCaptures } from "./league-captures";
import {
  composeOverrideMedia,
  composePreviewMedia,
} from "./compose-preview-media";
import { type StorePagePublic } from "@/features/store-editor/records";
import type { LeagueSummary } from "@softmax/observatory-client/types";
import { type WatchAddresses } from "@softmax/web-features/leagues";
import { type PreviewMediaItem } from "../content/preview-details.types";
import { replayStageUnfit } from "../copy/preview-copy";
import {
  readLatestReplay,
  readLeagueMatchFrames,
} from "./preview-replay-media.data";
import { readWikiImages } from "./preview-wiki-images.data";
export async function resolveMedia({
  signal,
  params,
}: {
  signal?: AbortSignal | undefined;
  params: {
    heroArt: "generals-board" | null;
    art: string | null;
    name: string;
    wikiName: string;
    coworldSlug: string;
    leagues: readonly LeagueSummary[];
    addresses: WatchAddresses;
    leagueId: string;
    storePagePromise: Promise<StorePagePublic | null>;
  };
}): Promise<PreviewMediaItem[]> {
  const {
    heroArt,
    art,
    name,
    wikiName,
    coworldSlug,
    leagues,
    addresses,
    leagueId,
    storePagePromise,
  } = params;
  const [replay, storePage] = await Promise.all([
    readLatestReplay({
      signal,
      leagueIds: [leagueId, ...leagues.map((l) => l.id)],
    }),
    storePagePromise,
  ]);
  const unfit = replay !== null && replayStageUnfit(coworldSlug);
  const heroMediaOverride = storePage?.content.hero_media ?? null;
  const showLiveReplay = storePage?.content.show_live_replay ?? true;

  if (heroMediaOverride && heroMediaOverride.length > 0) {
    return composeOverrideMedia({
      heroMediaOverride,
      showLiveReplay,
      replay,
      replayUnfit: unfit,
    });
  }

  const hasLiveBoard = heroArt === "generals-board";
  const screenshots: PreviewMediaItem[] = [];

  if (hasLiveBoard) {
    screenshots.push({ kind: "board", src: heroArt, alt: `${name}, live` });
  }

  const matchFrames = (
    await readLeagueMatchFrames({
      signal,
      leagues,
      leagueId,
    })
  ).filter((frame) => frame.src !== replay?.poster);
  screenshots.push(...matchFrames);

  if (!hasLiveBoard) {
    if (art && matchFrames.length === 0) {
      screenshots.push({
        kind: "capture",
        src: art,
        alt: `${name}, mid-episode`,
      });
    }

    screenshots.push(
      ...leagueCaptures({
        leagues,
        addresses,
      }),
    );
  }

  screenshots.push(
    ...(previewMedia[coworldSlug] ?? []).map((m): PreviewMediaItem => ({
      kind: "capture",
      ...m,
    })),
  );
  const illustrations =
    matchFrames.length > 0
      ? []
      : await readWikiImages({
          signal,
          wikiName,
        });
  return composePreviewMedia({
    replay,
    replayUnfit: unfit,
    screenshots,
    illustrations,
  });
}

const previewMedia: Record<
  string,
  readonly {
    src: string;
    alt: string;
  }[]
> = {
  proxywar: [
    {
      src: "/thumbs/proxywar-ffa-16p.webp",
      alt: "Proxy War, 16-player free-for-all, mid-episode",
    },
  ],
};
